/**
 * CIDR utility module for IPv4 only.
 * Uses 32-bit unsigned integer math for range checks.
 * ES Module version for React.
 */

const IPV4_OCTET = /^(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]|[0-9])$/;

/** Allowed private supernets for VCN and subnet CIDRs (/16 to /30) - full RFC1918 ranges */
export const ALLOWED_SUPERNETS = [
  { cidr: '10.0.0.0/8', start: 0x0a000000, end: 0x0affffff },   // 10.0.0.0/8
  { cidr: '172.16.0.0/12', start: 0xac100000, end: 0xac1fffff }, // 172.16.0.0/12
  { cidr: '192.168.0.0/16', start: 0xc0a80000, end: 0xc0a8ffff }, // 192.168.0.0/16
];

/**
 * Convert IPv4 string to unsigned 32-bit integer.
 * @param {string} ip - e.g. "10.0.1.0"
 * @returns {number|null} 0..0xffffffff or null if invalid
 */
export function ipToInt(ip) {
  if (!ip || typeof ip !== 'string') return null;
  const parts = ip.trim().split('.');
  if (parts.length !== 4) return null;
  let n = 0;
  for (let i = 0; i < 4; i++) {
    const p = parts[i].trim();
    if (!IPV4_OCTET.test(p)) return null;
    n = (n << 8) | (parseInt(p, 10) >>> 0);
  }
  return n >>> 0;
}

/**
 * Parse CIDR string into range (network start, broadcast end).
 * IPv4 only; rejects IPv6 (e.g. contains ':').
 * @param {string} cidr - e.g. "10.0.0.0/16"
 * @returns {{ ipInt: number, prefix: number, start: number, end: number } | null}
 */
export function parseCidr(cidr) {
  if (!cidr || typeof cidr !== 'string') return null;
  const s = cidr.trim();
  if (s.includes(':')) return null; // IPv6
  const slashIdx = s.indexOf('/');
  if (slashIdx === -1) return null;
  const base = s.slice(0, slashIdx).trim();
  const prefixStr = s.slice(slashIdx + 1).trim();
  if (!/^\d+$/.test(prefixStr)) return null;
  const prefix = parseInt(prefixStr, 10);
  if (prefix < 0 || prefix > 32) return null;
  const ipInt = ipToInt(base);
  if (ipInt === null) return null;
  const size = 1 << (32 - prefix);
  const start = (ipInt >>> (32 - prefix)) << (32 - prefix);
  const end = start + size - 1;
  return { ipInt, prefix, start: start >>> 0, end: end >>> 0 };
}

/**
 * Valid prefix length: /16 through /30 only.
 * @param {number} prefix
 * @returns {boolean}
 */
export function isValidPrefix(prefix) {
  return Number.isInteger(prefix) && prefix >= 16 && prefix <= 30;
}

/**
 * Check if CIDR is fully within an allowed private supernet.
 * @param {string} cidr
 * @returns {boolean}
 */
export function isWithinAllowedSupernets(cidr) {
  const parsed = parseCidr(cidr);
  if (!parsed || !isValidPrefix(parsed.prefix)) return false;

  for (const supernet of ALLOWED_SUPERNETS) {
    if (parsed.start >= supernet.start && parsed.end <= supernet.end) {
      return true;
    }
  }
  return false;
}

/**
 * Check if a base CIDR is valid.
 * Base CIDRs must be /16 to /24 (same as OCI VCN restrictions)
 * and must be within RFC1918 private ranges.
 * @param {string} cidr
 * @returns {boolean}
 */
export function isValidBaseCidr(cidr) {
  const parsed = parseCidr(cidr);
  if (!parsed) return false;

  // Base CIDRs: /16 to /24 (OCI VCN restriction)
  if (parsed.prefix < 16 || parsed.prefix > 24) return false;

  // Must be within RFC1918 ranges (10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16)
  // 10.0.0.0/8
  if (parsed.start >= 0x0a000000 && parsed.end <= 0x0affffff) return true;
  // 172.16.0.0/12 (172.16.0.0 - 172.31.255.255)
  if (parsed.start >= 0xac100000 && parsed.end <= 0xac1fffff) return true;
  // 192.168.0.0/16
  if (parsed.start >= 0xc0a80000 && parsed.end <= 0xc0a8ffff) return true;

  return false;
}

/**
 * Check if childCidr is fully contained in parentCidr.
 * @param {string} parentCidr
 * @param {string} childCidr
 * @returns {boolean}
 */
export function contains(parentCidr, childCidr) {
  const p = parseCidr(parentCidr);
  const c = parseCidr(childCidr);
  if (!p || !c) return false;
  return c.start >= p.start && c.end <= p.end;
}

/**
 * Check if two CIDR ranges overlap (identical, one contained, or partial).
 * @param {string} cidrA
 * @param {string} cidrB
 * @returns {boolean}
 */
export function overlaps(cidrA, cidrB) {
  const a = parseCidr(cidrA);
  const b = parseCidr(cidrB);
  if (!a || !b) return false;
  return a.start <= b.end && b.start <= a.end;
}

/**
 * Normalize CIDR to canonical form "x.x.x.x/prefix" (network address).
 * Returns null if invalid (e.g. IPv6, bad format).
 * @param {string} cidr
 * @returns {string|null}
 */
export function normalize(cidr) {
  const parsed = parseCidr(cidr);
  if (!parsed) return null;
  const a = (parsed.start >>> 24) & 0xff;
  const b = (parsed.start >>> 16) & 0xff;
  const c = (parsed.start >>> 8) & 0xff;
  const d = parsed.start & 0xff;
  return `${a}.${b}.${c}.${d}/${parsed.prefix}`;
}

/**
 * Convert a 32-bit unsigned integer to dotted-decimal IPv4 string.
 * @param {number} n - 32-bit unsigned integer
 * @returns {string}
 */
export function formatIp(n) {
  const u = n >>> 0;
  return `${(u >>> 24) & 0xff}.${(u >>> 16) & 0xff}.${(u >>> 8) & 0xff}.${u & 0xff}`;
}

/**
 * Convert CIDR prefix to subnet mask string.
 * @param {number} prefix - CIDR prefix (0-32)
 * @returns {string} - e.g., "255.255.255.0" for /24
 */
export function prefixToMask(prefix) {
  if (prefix < 0 || prefix > 32) return '0.0.0.0';
  const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
  return formatIp(mask);
}

/**
 * Split a parent CIDR into sequential equal-sized child blocks.
 * Returns at most `count` non-overlapping blocks that fit within the parent.
 * Used by the F47 CIDR proposal algorithm.
 * @param {string} parentCidr - e.g. "10.0.0.0/16"
 * @param {number} childPrefix - e.g. 20 (must be > parent prefix)
 * @param {number} count - max number of blocks to return
 * @returns {string[]} array of CIDR strings in order
 */
export function splitCidr(parentCidr, childPrefix, count) {
  const p = parseCidr(parentCidr);
  // childPrefix == p.prefix is valid: returns the parent block itself (one block)
  if (!p || childPrefix < p.prefix || childPrefix > 32) return [];
  // blockSize is at most 2^16 = 65536 (prefix >= 16), safe for 32-bit math
  const blockSize = 1 << (32 - childPrefix);
  const result = [];
  let current = p.start >>> 0;
  for (let i = 0; i < count; i++) {
    const end = (current + blockSize - 1) >>> 0;
    if (end > p.end) break;
    result.push(`${formatIp(current)}/${childPrefix}`);
    current = (current + blockSize) >>> 0;
  }
  return result;
}

/**
 * Format a number with locale-specific separators.
 * @param {number} n
 * @returns {string}
 */
export function formatNum(n) {
  return n.toLocaleString();
}
